import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Loader2, BookOpen, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EditionMergeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentBookId: string;
  currentBookTitle: string;
  onMerge: (newBookId: string) => void;
}

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  authors: string[];
  cover_url?: string;
  published_year?: number;
  isbn13?: string;
  isbn10?: string;
}

export function EditionMergeDialog({ 
  isOpen, 
  onClose, 
  currentBookId, 
  currentBookTitle,
  onMerge 
}: EditionMergeDialogProps) {
  const [searchQuery, setSearchQuery] = useState(currentBookTitle);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { 
          query: searchQuery,
          limit: 10
        },
      });

      if (error) throw error;

      // Filter out the current book from results
      const results = (data?.results || []).filter(
        (book: SearchResult) => book.id !== currentBookId
      );
      
      setSearchResults(results);

      if (results.length === 0) {
        toast.error('No other editions found', {
          description: 'Try searching with a different query'
        });
      }
    } catch (error) {
      console.error('[EditionMerge] Search error:', error);
      toast.error('Search failed', {
        description: 'Please try again'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleMerge = async (targetBook: SearchResult) => {
    setIsMerging(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Import the cleanup utility
      const { cleanupDuplicateBook } = await import('@/utils/cleanupDuplicateBook');

      // Merge data from current book to target book
      await cleanupDuplicateBook(user.id, currentBookId, targetBook.id);

      toast.success('Editions merged!', {
        description: `Your data has been moved to "${targetBook.title}"`
      });

      onMerge(targetBook.id);
      onClose();
    } catch (error) {
      console.error('[EditionMerge] Merge error:', error);
      toast.error('Merge failed', {
        description: 'Please try again or contact support'
      });
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Find Correct Edition</DialogTitle>
          <DialogDescription>
            Search for the correct edition of this book. Your rating, review, and all data will be transferred.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This will merge all your data (rating, review, tags, etc.) to the selected edition.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Input
            placeholder="Search for the correct edition..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            disabled={isSearching || isMerging}
          />
          <Button 
            onClick={handleSearch} 
            disabled={isSearching || isMerging || !searchQuery.trim()}
          >
            {isSearching ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search
              </>
            )}
          </Button>
        </div>

        <ScrollArea className="flex-1 h-[400px]">
          {searchResults.length === 0 && !isSearching && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <BookOpen className="w-12 h-12 mb-2 opacity-50" />
              <p className="text-sm">Search for other editions</p>
            </div>
          )}

          <div className="space-y-2 pr-4">
            {searchResults.map((book) => (
              <Card key={book.id} className="p-4">
                <div className="flex gap-4">
                  {book.cover_url && (
                    <img 
                      src={book.cover_url} 
                      alt={book.title}
                      className="w-16 h-24 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{book.title}</h3>
                    {book.subtitle && (
                      <p className="text-sm text-muted-foreground truncate">{book.subtitle}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {book.authors?.join(', ') || 'Unknown author'}
                    </p>
                    <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                      {book.published_year && <span>{book.published_year}</span>}
                      {book.isbn13 && <span>ISBN: {book.isbn13}</span>}
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => handleMerge(book)}
                    disabled={isMerging}
                  >
                    {isMerging ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Merge to This'
                    )}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
